import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Chip,
  Stack,
  CircularProgress,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { capitalCase } from 'change-case';
import OrganizationResultDialog from './OrganizationResultDialog';
import { useSnackbar } from 'notistack';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import {
  activateFhirOrganization,
  deactivateFhirOrganization,
} from '../../service/OrganizationService';

const OrganizationStatusInfo = ({
  name,
  active,
  translate,
}: {
  name: string;
  active: boolean;
  translate: any;
}) => (
  <Box mb={3}>
    <Stack spacing={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{name}</Typography>
        <Chip
          label={capitalCase(
            active ? translate('details.general.active') : translate('details.general.inactive')
          )}
          color={(active && 'info') || 'error'}
        />
      </Box>

      <Typography variant="body2" color="text.secondary">
        {active
          ? translate('details.activationFlow.statusActive')
          : translate('details.activationFlow.statusDeactivate')}
      </Typography>

      <Alert severity={active ? 'warning' : 'info'}>
        {active ? (
          <>
            {translate('details.activationFlow.dialogDeactivating.title')}
            <ul>
              <li>{translate('details.activationFlow.dialogDeactivating.dialogOne')}</li>
              <li>{translate('details.activationFlow.dialogDeactivating.dialogTwo')}</li>
              <li>{translate('details.activationFlow.dialogDeactivating.dialogThree')}</li>
              <li>{translate('details.activationFlow.dialogDeactivating.dialogFour')}</li>
            </ul>
          </>
        ) : (
          <>{translate('details.activationFlow.dialogActivating')}</>
        )}
      </Alert>
    </Stack>
  </Box>
);

export default function OrganizationActivationFlow({ organization, refresh, translate }: any) {
  const { enqueueSnackbar } = useSnackbar();

  const [openConfirm, setOpenConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [openResult, setOpenResult] = useState(false);

  const handleAction = async () => {
    setLoading(true);
    try {
      let res: any = null;
      if (organization?.active) {
        res = await deactivateFhirOrganization(organization?.id);
      } else {
        res = await activateFhirOrganization(organization?.id);
      }
      setResult(res);
      setOpenResult(true);
    } catch (error) {
      console.log(error);
      enqueueSnackbar('An error has occurred', { variant: 'error' });
    } finally {
      setLoading(false);
      setOpenConfirm(false);
    }
  };

  const handleClose = () => {
    setOpenResult(false);
    refresh();
  };

  const { isAllowedActivate, isAllowedDeactivate } = {
    isAllowedActivate: checkAclValidation({
      acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.ACTIVATE],
    }),
    isAllowedDeactivate: checkAclValidation({
      acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.DEACTIVATE],
    }),
  };

  const disabled = loading
    ? loading
    : organization?.active
    ? isAllowedActivate
    : !organization?.active
    ? isAllowedDeactivate
    : true;

  return (
    <>
      <OrganizationStatusInfo
        name={organization.name}
        active={organization.active}
        translate={translate}
      />

      <Button
        variant="contained"
        color={organization.active ? 'error' : 'secondary'}
        startIcon={<Iconify icon={organization.active ? 'nrk:close-active' : 'nrk:check-active'} />}
        onClick={() => setOpenConfirm(true)}
        disabled={!disabled}
      >
        {organization.active
          ? translate('details.activationFlow.deactivate')
          : translate('details.activationFlow.activate')}
      </Button>

      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
        <DialogTitle>
          {organization.active
            ? translate('details.activationFlow.dialogTitleD')
            : translate('details.activationFlow.dialogTitleA')}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#dae1e824' }}>
          <Stack spacing={2}>
            <Typography>
              {organization.active
                ? translate('details.activationFlow.dialogDeactivate')
                : translate('details.activationFlow.dialogActivate')}
            </Typography>
            {organization.active && (
              <Alert severity="warning">{translate('details.activationFlow.dialogActive')}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAction}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading
              ? translate('details.activationFlow.processing')
              : translate('details.activationFlow.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <OrganizationResultDialog
        open={openResult}
        onClose={handleClose}
        organizationName={organization?.name}
        data={result}
        translate={translate}
      />
    </>
  );
}
